import React from 'react';
import {Star, ChevronLeft } from 'lucide-react';
import { HERO_IMAGES } from '../utils/imageUrls';
import SearchBar from "./SearchBar";

function HeroSection({ scrollToSection, PergolasAndDecksRef, cardsRef, middelRef, ourProjects }) {
    return (
        <div className="bg-background min-h-screen pt-10 pb-16 px-4 sm:px-6 lg:px-8">

            {/* --- שכבת החיפוש --- */}
            <div className="max-w-7xl mx-auto mb-10 flex flex-col items-center">
                <h1 className="text-3xl md:text-4xl font-medium text-text-primary mb-6 tracking-wide text-center">
                    מה הפרויקט הבא שלך?
                </h1>
                <div className="w-full max-w-2xl relative z-20">
                    <SearchBar
                        homeFurnitureRef={cardsRef}
                        gardenFurnitureRef={middelRef}
                        pergolasAndDecksRef={PergolasAndDecksRef}
                        ourProjectsRef={ourProjects}
                    />
                </div>
            </div>

            {/* --- הגריד הראשי --- */}
            <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 z-10 relative">

                {/* שורה 1, פריט 1: פרגולות (66%) */}
                <div
                    onClick={() => scrollToSection(PergolasAndDecksRef)}
                    className="md:col-span-2 relative rounded-2xl overflow-hidden group cursor-pointer shadow-soft hover:shadow-hover transition-all duration-300 aspect-video md:aspect-[2/1]"
                >
                    <img src={HERO_IMAGES.pergola} alt="פרגולות מעוצבות" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90" />
                    <div className="absolute bottom-0 right-0 p-6 md:p-8">
                        <h2 className="text-2xl md:text-4xl font-medium text-white mb-2">פרגולות מעוצבות</h2>
                        <p className="text-white/80 text-base md:text-lg font-light">חלומות הופכים למציאות.</p>
                    </div>
                </div>

                {/* שורה 1, פריט 2: בתי עץ (33%) */}
                <div
                    onClick={() => scrollToSection(cardsRef)}
                    className="md:col-span-1 relative rounded-2xl overflow-hidden group cursor-pointer shadow-soft hover:shadow-hover transition-all duration-300 aspect-square md:aspect-auto md:h-full"
                >
                    <img src={HERO_IMAGES.homeFurniture} alt="בתי עץ" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-0 right-0 p-6">
                        <h2 className="text-xl md:text-2xl font-medium text-white mb-1">בתי עץ לילדים</h2>
                        <p className="text-white/80 text-sm font-light">עולם של דמיון</p>
                    </div>
                </div>

                {/* --- שורה 2: 3 מלבנים (33% כל אחד) --- */}

                {/* מלבן תחתון 1: נדנדות */}
                <div
                    onClick={() => scrollToSection(middelRef)}
                    className="md:col-span-1 relative rounded-2xl overflow-hidden group cursor-pointer shadow-soft hover:shadow-hover transition-all duration-300 aspect-[16/10]"
                >
                    <img src={HERO_IMAGES.gardenFurniture} alt="נדנדות" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-0 right-0 p-5">
                        <h2 className="text-lg md:text-xl font-medium text-white">נדנדות עץ</h2>
                    </div>
                </div>

                {/* מלבן תחתון 3: פרויקטים */}
                <div
                    onClick={() => scrollToSection(ourProjects)}
                    className="md:col-span-1 relative rounded-2xl overflow-hidden group cursor-pointer shadow-soft hover:shadow-hover transition-all duration-300 aspect-[16/10]"
                >
                    <img src={HERO_IMAGES.ourProjects} alt="פרויקטים" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                    <div className="absolute bottom-0 right-0 p-5">
                        <h2 className="text-lg md:text-xl font-medium text-white">הפרויקטים האחרונים</h2>
                    </div>
                </div>
                {/* מלבן תחתון 2: *** קוביית הביקורת וה-CTA החדשה *** */}
                <div className="md:col-span-1 relative rounded-2xl overflow-hidden shadow-soft hover:shadow-hover transition-all duration-300 aspect-[16/10] bg-surface border border-secondary p-5 flex flex-col justify-between">

                    {/* חלק עליון: פרופיל ודירוג */}
                    <div className="flex items-center gap-3 mb-2">
                        {/* תמונת פרופיל (אפשר לשים תמונה אמיתית, כרגע Placeholder נקי) */}
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-primary font-bold text-sm">יו"ר</span>
                        </div>
                        <div>
                            <div className="font-medium text-text-primary text-sm">יעל ורן</div>
                            <div className="flex gap-0.5 mt-0.5">
                                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3 h-3 text-amber-500" fill="currentColor" />)}
                            </div>
                        </div>
                    </div>

                    {/* חלק אמצעי: טקסט הביקורת (מוגבל לשורות כדי לא לגלוש) */}
                    <p className="text-text-secondary text-sm leading-snug line-clamp-2 italic">
                        "העבודה בוצעה במקצועיות, וגימור ברמה הכי גבוהה שיש. ממליצים בחום!"
                    </p>

                    {/* חלק תחתון: כפתור הנעה לפעולה (CTA) */}
                    <div className="mt-3">
                        <button
                            onClick={() => window.location.href = '/contactUs'} // או פתיחת פופאפ
                            className="w-full py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary-light transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                        >
                            לקבלת הצעת מחיר
                        </button>
                    </div>
                </div>
            </div>

            {/* הערה: הסרנו את פס הביקורות הארוך שהיה כאן קודם! */}

            {/* --- חלק תחתון (חצי חצי): פרויקטים מול תהליך עבודה --- */}
            <div className="max-w-7xl mx-auto mt-20 mb-12">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-16">

                    {/* הפרויקטים האחרונים */}
                    <div>
                        <div className="flex justify-between items-end mb-8">
                            <h3 className="text-2xl font-medium text-text-primary border-r-4 border-primary pr-3">הפרויקטים האחרונים</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-secondary shadow-soft">
                                <img src={HERO_IMAGES.pergola} alt="פרויקט" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                            </div>
                            <div className="aspect-[4/3] rounded-xl overflow-hidden bg-secondary shadow-soft">
                                <img src={HERO_IMAGES.homeFurniture} alt="פרויקט" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                            </div>
                        </div>
                    </div>

                    {/* איך אנחנו עובדים (תהליך אופקי) */}
                    <div>
                        <h3 className="text-2xl font-medium text-text-primary border-r-4 border-primary pr-3 mb-8">איך אנחנו עובדים?</h3>

                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-12">
                            {/* שלב 1 */}
                            <div className="flex flex-col items-center text-center flex-1">
                                <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold mb-4 shadow-md">1</div>
                                <h4 className="font-medium text-lg text-text-primary">פגישת תכנון</h4>
                                <p className="text-text-secondary text-sm mt-2 px-2">מדידות, השראה ובחירת חומרים</p>
                            </div>

                            <ChevronLeft className="w-8 h-8 text-secondary-dark hidden sm:block shrink-0" />

                            {/* שלב 2 */}
                            <div className="flex flex-col items-center text-center flex-1">
                                <div className="w-14 h-14 rounded-full bg-surface border-2 border-primary text-primary flex items-center justify-center text-xl font-bold mb-4 shadow-sm">2</div>
                                <h4 className="font-medium text-lg text-text-primary">ייצור בסדנא</h4>
                                <p className="text-text-secondary text-sm mt-2 px-2">חיתוך וגימור מוקפד בעץ מלא</p>
                            </div>

                            <ChevronLeft className="w-8 h-8 text-secondary-dark hidden sm:block shrink-0" />

                            {/* שלב 3 */}
                            <div className="flex flex-col items-center text-center flex-1">
                                <div className="w-14 h-14 rounded-full bg-surface border-2 border-primary text-primary flex items-center justify-center text-xl font-bold mb-4 shadow-sm">3</div>
                                <h4 className="font-medium text-lg text-text-primary">התקנה בבית</h4>
                                <p className="text-text-secondary text-sm mt-2 px-2">הקמה חלקה לשביעות רצונכם</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
}

export default HeroSection;